/**
 * De opzet van een potje.
 *
 * Elke speler begint met twee dorpen, een straat ertussen en zes landschappen —
 * één van elke soort, elk met één grondstof erop. Zie kennisbank 02 §1.
 *
 * Wat hier nog niet gebeurt: het kiezen van de drie handkaarten. Dat is een keuze van
 * de speler (elk uit een andere stapel) en dus een zet, geen opzet.
 */
import { CURRENT_VERSIONS, LIMITS, type Seat } from '@catan/contracts';
import { createRng, shuffle, type Rng } from './rng.js';
import type { GameState, LandscapeTile, PlayerState, Principality, Side, StartLandschap } from './state.js';

/**
 * De plaatsen voor de zes landschappen bij aanvang: drie boven, drie onder, op de
 * even kolommen 0, 2 en 4 (zie de tekening in state.ts).
 */
const START_POSITIONS: readonly { column: number; side: Side }[] = [
  { column: 0, side: 'boven' },
  { column: 2, side: 'boven' },
  { column: 4, side: 'boven' },
  { column: 0, side: 'onder' },
  { column: 2, side: 'onder' },
  { column: 4, side: 'onder' },
];

/**
 * De landschapstapel: alle zesendertig combinaties van landschap en dobbelgetal.
 *
 * Het handboek noemt de stapel ("alle kaarten van de landschapstapel bekijken", p. 46)
 * maar niet zijn samenstelling. Zesendertig kaarten dekt elk landschap met elk getal
 * precies één keer — genoeg om nooit op te raken (samen trekken de spelers er hoogstens
 * twaalf) en overzichtelijk genoeg om door te bladeren.
 *
 * De zes landschappen waarmee elke speler begint komen hier níét uit: die deelt de opzet
 * apart uit, en wel zo dat iedereen elk landschap en elk getal één keer krijgt. Dat
 * evenwicht is het waard; dat een landschap daardoor twee keer in het spel kan zitten is
 * geen bezwaar, want twee bossen naast elkaar is een doodgewone stand.
 *
 * Dit is een reconstructie; zie kennisbank 05.
 */
function landschapStapel(): StartLandschap[] {
  // "11 landen met op de achterzijde een boom: 2 akkers, bergen, weide, bos en heuvels;
  // 1 rivierbedding" (handboek p. 16). De getallen komen van de fysieke kaarten zelf,
  // aangeleverd door Thijs (30-07-2026) — hier stond kort een beredeneerde reconstructie,
  // en daarvóór een 6×6-stapel van 36 die nooit opraakte. De toets `landenstapel` pint de
  // echte elf vast.
  return [
    { landscape: 'akker', dieNumber: 3 },
    { landscape: 'akker', dieNumber: 4 },
    { landscape: 'bos', dieNumber: 1 },
    { landscape: 'bos', dieNumber: 6 },
    { landscape: 'bergen', dieNumber: 5 },
    { landscape: 'bergen', dieNumber: 4 },
    { landscape: 'weide', dieNumber: 6 },
    { landscape: 'weide', dieNumber: 5 },
    { landscape: 'heuvels', dieNumber: 1 },
    { landscape: 'heuvels', dieNumber: 2 },
    { landscape: 'rivierbedding', dieNumber: 3 },
  ];
}

export interface SetupOptions {
  readonly seed: string;
  readonly names: readonly [string, string];
  /** Wie begint. Weglaten laat de generator loten, zoals het origineel doet. */
  readonly startingSeat?: Seat;
  /**
   * Verdeelt de speler zijn startlandschappen zelf?
   *
   * Zo ja, dan begint zijn bord **leeg** en zitten de zes landschappen in de voorraad —
   * precies wat het origineel doet als je op "ja" klikt. Zo nee, dan liggen ze meteen
   * verdeeld. De computer verdeelt nooit zelf.
   */
  readonly zelfVerdelen?: readonly [boolean, boolean];
}

/**
 * De twee startsets van het fysieke spel — de "wapens" van p. 16, met de vaste paring
 * van landschap en getal zoals die op de echte kaarten gedrukt staat (aangeleverd door
 * Thijs, 30-07-2026). Stoel 0 speelt de rode set, stoel 1 de blauwe. Tot nu toe werden
 * landschappen én getallen hier geschud: de invariant (elk landschap één keer, elk getal
 * één keer) klopte, de paring niet — en de paring stuurt de strategie, want rood oogst
 * zijn graan op 1 en blauw pas op 2.
 */
const START_SETS: readonly [readonly StartLandschap[], readonly StartLandschap[]] = [
  // Rood: graan 1, erts 2, wol 3, hout 4, steen 5, goud 6.
  [
    { landscape: 'akker', dieNumber: 1 },
    { landscape: 'bergen', dieNumber: 2 },
    { landscape: 'weide', dieNumber: 3 },
    { landscape: 'bos', dieNumber: 4 },
    { landscape: 'heuvels', dieNumber: 5 },
    { landscape: 'rivierbedding', dieNumber: 6 },
  ],
  // Blauw: goud 1, graan 2, erts 3, wol 4, hout 5, steen 6.
  [
    { landscape: 'rivierbedding', dieNumber: 1 },
    { landscape: 'akker', dieNumber: 2 },
    { landscape: 'bergen', dieNumber: 3 },
    { landscape: 'weide', dieNumber: 4 },
    { landscape: 'bos', dieNumber: 5 },
    { landscape: 'heuvels', dieNumber: 6 },
  ],
];

/**
 * Legt de startset van deze stoel neer. De paring landschap–getal ligt vast (zie
 * `START_SETS`); alleen de plékken zijn vrij — "De spelers kiezen waar ze hun landen
 * leggen" (p. 9) — dus de automaat schudt de volgorde over de zes posities, en wie zelf
 * verdeelt kiest ze stuk voor stuk.
 */
function buildPrincipality(rng: Rng, seat: Seat): { rng: Rng; principality: Principality } {
  const volgorde = shuffle(rng, START_SETS[seat]);

  const landscapes: LandscapeTile[] = START_POSITIONS.map((position, index) => ({
    landscape: (volgorde.value[index] as StartLandschap).landscape,
    column: position.column,
    side: position.side,
    dieNumber: (volgorde.value[index] as StartLandschap).dieNumber,
    // Elk landschap begint met één grondstof (handboek p. 10; fysieke regels 1996).
    resources: 1,
  }));

  return {
    rng: volgorde.rng,
    principality: {
      buildings: [
        { kind: 'dorp', column: 1, expansions: { boven: [], onder: [] } },
        { kind: 'dorp', column: 3, expansions: { boven: [], onder: [] } },
      ],
      roads: [{ column: 2 }],
      landscapes,
    },
  };
}

/**
 * Verdeelt de uitbouwkaarten over vijf stapels.
 *
 * Het origineel deelt 62 kaarten in stapels van 13, 13, 12, 13 en 11 — geen gelijke
 * verdeling dus. De fysieke regels zeggen "ongeveer gelijk, twee stapels hebben er één
 * meer". Wij volgen de digitale verdeling, want die telden we in de referentie.
 */
export function dealStacks(rng: Rng, cardIds: readonly number[]): {
  rng: Rng;
  stacks: number[][];
} {
  const sizes = [13, 13, 12, 13, 11];
  const total = sizes.reduce((a, b) => a + b, 0);
  if (cardIds.length !== total) {
    throw new Error(`verwacht ${total} uitbouwkaarten, kreeg er ${cardIds.length}`);
  }
  const shuffled = shuffle(rng, cardIds);
  const stacks: number[][] = [];
  let offset = 0;
  for (const size of sizes) {
    stacks.push(shuffled.value.slice(offset, offset + size));
    offset += size;
  }
  return { rng: shuffled.rng, stacks };
}

/**
 * Zet een nieuw potje op.
 *
 * @param cardIds  alle exemplaren van de uitbouwkaarten (62 stuks)
 * @param eventIds alle gebeurteniskaarten
 */
export function createGame(
  options: SetupOptions,
  cardIds: readonly number[],
  eventIds: readonly number[],
): GameState {
  let rng = createRng(options.seed);

  const players: PlayerState[] = [];
  const voorraad: StartLandschap[][] = [[], []];
  for (const seat of [0, 1] as const) {
    const built = buildPrincipality(rng, seat);
    rng = built.rng;
    let principality = built.principality;
    if (options.zelfVerdelen?.[seat] === true) {
      // Zelf verdelen: het bord begint leeg en de zes landschappen gaan naar de voorraad.
      voorraad[seat] = principality.landscapes.map((t) => ({
        landscape: t.landscape,
        dieNumber: t.dieNumber,
      }));
      principality = { ...principality, landscapes: [] };
    }
    players.push({
      seat,
      name: options.names[seat],
      principality,
      hand: [],
      handLimit: LIMITS.baseHandLimit,
    });
  }

  const geschud = shuffle(rng, landschapStapel());
  rng = geschud.rng;

  const dealt = dealStacks(rng, cardIds);
  rng = dealt.rng;

  const events = shuffle(rng, eventIds);
  rng = events.rng;

  let startingSeat = options.startingSeat;
  if (startingSeat === undefined) {
    const draw = shuffle(rng, [0, 1] as const);
    rng = draw.rng;
    startingSeat = draw.value[0] as Seat;
  }

  return {
    rulesVersion: CURRENT_VERSIONS.rules,
    cardsVersion: CURRENT_VERSIONS.cards,
    rng,
    players: [players[0] as PlayerState, players[1] as PlayerState],
    turn: startingSeat,
    turnNumber: 0,
    phase: 'opzet',
    dice: undefined,
    pending: undefined,
    stacks: dealt.stacks,
    eventDeck: events.value,
    discard: [],
    knightToken: undefined,
    windmillToken: undefined,
    setupPool: [voorraad[0] as StartLandschap[], voorraad[1] as StartLandschap[]],
    lastEventCard: undefined,
    offersThisTurn: [0, 0],
    kaartGeruild: false,
    afgewezenAanbiedingen: [],
    winner: undefined,
    forcedProduction: undefined,
    landscapeDeck: geschud.value,
    offer: undefined,
    produced: [0, 0],
  };
}
